import React from 'react';
import { FormattedMessage } from 'react-intl';
import { GlobalHeader as DbxGlobalHeader } from 'dig-components/global_header';
import { Menu } from 'dig-components/menu';
import { Button } from 'dig-components/buttons';
import { Truncate } from 'dig-components/truncate';
import { Avatar, avatarColorForUserIdentifier } from 'dig-components/avatar';
import { Text } from 'dig-components/typography';

import Spacer, { SpacerSize } from 'hellospa/foundation/spacing/spacer';
import { getInitials } from 'hellospa/common/i18n/user';
import { RebrandTheme } from 'hello-react/components/rebrand';
import HfReactHelper from 'hellosign/components/hf-react-helper';
import SignatureModal from './signature-modal';

import ReferModal from './refer-modal';
import styles from './account-menu.scss';

type SiteCode = typeof HfReactHelper.HfSites.SITE_CODES[number];

export type GlobalHeaderAccountMenuTypes = {
  app?: any,
  siteCode: SiteCode,
  locale: string,
  emailAddress: string,
  showUpgradeButton: boolean,
  numDocsLeft: number | null | 'Unlimited',
  numFaxPagesLeft: number | null | 'Unlimited',
  numApiDocsLeft: number | null | 'Unlimited',
  numTemplatesLeft: number | null | 'Unlimited',
  showApiCredits: boolean,
  canAccessAdminConsole: boolean,
  signatureModalProps: Object,

  // Optional
  name?: string,
  referUrl?: string,
  primarySignatureGuid?: string,
};

function GlobalHeaderAccountMenu({
  app,
  siteCode,
  locale,
  emailAddress,
  name,
  numDocsLeft,
  numFaxPagesLeft,
  numApiDocsLeft,
  numTemplatesLeft,
  primarySignatureGuid,
  showApiCredits,
  canAccessAdminConsole,
  signatureModalProps,
  showUpgradeButton,
  referUrl,
}: GlobalHeaderAccountMenuTypes) {
  const [isSignatureModalOpen, setisSignatureModalOpen] = React.useState(false);
  const [isReferModalOpen, setIsReferModalOpen] = React.useState(false);
  const site = React.useMemo(() => HfReactHelper.HfSites.getSite(siteCode), [siteCode]);
  const isSign = React.useMemo(() => HfReactHelper.HfSites.isHelloSign(site), [site]);
  const isFax = React.useMemo(() => HfReactHelper.HfSites.isHelloFax(site), [site]);

  // Use initials based on name, or email if no name is set.
  const initials = React.useMemo(() => {
    if (name != null && name.length > 0) {
      return getInitials(name, locale);
    } else {
      return getInitials(emailAddress, locale);
    }
  }, [locale, name, emailAddress]);

  const avatarColor = React.useMemo(() => avatarColorForUserIdentifier(initials), [initials]);

  const showSignatureModal = React.useCallback(() => {
    setisSignatureModalOpen(true);
  }, []);

  // Logic copied from old account menu.
  let signatureGuidToUse;
  if (window.primarySignatureGuid === null) {
    // In this case we don't have a primary signature because it just got deleted
    signatureGuidToUse = null;
  } else {
    signatureGuidToUse = window.primarySignatureGuid || primarySignatureGuid;
  }

  return (
    <DbxGlobalHeader.AccountMenu
      initials={initials}
      backgroundColor={avatarColor}
      data-qa-ref="global-acct-menu"
    >
      {/* This content is portaled and requires new rebrand theme context */}
      <RebrandTheme>
        <div className={styles.accountMenu}>
          <Menu.Segment>
            <Menu.Row
              withLeftAccessory={
                <Avatar
                  size="large"
                  backgroundColor={avatarColor}
                >
                  <span data-qa-ref="global-acct-menu-user-initials">
                    {initials}
                  </span>
                </Avatar>
              }
              withSubtitle={
                <Truncate tooltipControlProps={{ auto: true, placement: 'left' }}>
                  <span data-qa-ref="global-acct-menu-user-email">
                    {emailAddress}
                  </span>
                </Truncate>
              }
            >
              {(name != null && name.length > 0) && (
                <span data-qa-ref="global-acct-menu-user-name">
                  {name}
                </span>
              )}
            </Menu.Row>
            {isSign && (
              <>
                <Menu.Row>
                  {showApiCredits && (
                    <>
                      <Text size="xsmall" color="faint" data-qa-ref="global-acct-menu-api-credits">
                        {typeof numApiDocsLeft === 'number' ? (
                          <FormattedMessage
                            id=""
                            description="Text which informs the user how many API signature requests they have remaining"
                            defaultMessage="{numApiDocsLeft, plural, =1 {# API signature request} other {# API signature requests}} remaining"
                            values={{
                              numApiDocsLeft,
                            }} />
                        ) : (
                          <FormattedMessage
                            id=""
                            description="Text which informs the user they have unlimited API signature requests"
                            defaultMessage="Unlimited API signature requests remaining" />
                        )}
                      </Text>
                      <br />
                    </>
                  )}
                  <Text size="xsmall" color="faint" data-qa-ref="global-acct-menu-requests-remaining">
                    {typeof numDocsLeft === 'number' ? (
                      <FormattedMessage
                        id=""
                        description="Text which informs the user how many signature requests they have remaining"
                        defaultMessage="{numDocsLeft, plural, =1 {# signature request} other {# signature requests}} remaining"
                        values={{
                          numDocsLeft,
                        }} />
                    ) : (
                      <FormattedMessage
                        id=""
                        description="Text which informs the user they have unlimited signature requests"
                        defaultMessage="Unlimited signature requests remaining" />
                    )}
                  </Text>
                  <br />
                  <Text size="xsmall" color="faint" data-qa-ref="global-acct-menu-templates-remaining">
                    {typeof numTemplatesLeft === 'number' ? (
                      <FormattedMessage
                        id=""
                        description="Text which informs the user how many templates they have remaining"
                        defaultMessage="{numTemplatesLeft, plural, =1 {# template} other {# templates}} remaining"
                        values={{
                          numTemplatesLeft,
                        }} />
                    ) : (
                      <FormattedMessage
                        id=""
                        description="Text which informs the user they have unlimited templates"
                        defaultMessage="Unlimited templates remaining" />
                    )}
                  </Text>
                </Menu.Row>
                {showUpgradeButton && (
                  <Menu.Row>
                    <Button
                      variant="transparent"
                      onClick={() => setIsReferModalOpen(true)}
                      data-qa-ref="global-acct-menu-get-free-documents"
                    >
                      <FormattedMessage
                        id=""
                        description="Text for a button that when pressed will bring open the Refer-A-Friend modal."
                        defaultMessage="Get free documents" />
                    </Button>
                    <ReferModal
                      open={isReferModalOpen}
                      // We know referUrl is a string if `showUpgradeButton` is true.
                      referUrl={referUrl as string}
                      onRequestClose={() => setIsReferModalOpen(false)} />
                  </Menu.Row>
                )}
              </>
            )}

            {isFax && (
              <>
                <Menu.Row>
                  <Text size="small" color="faint" data-qa-ref="global-acct-menu-faxes-remaining">
                    {typeof numTemplatesLeft === 'number' ? (
                      <FormattedMessage
                        id=""
                        description="Text which informs the user how many faxes they have remaining"
                        defaultMessage="{numFaxPagesLeft, plural, =1 {# fax page} other {# fax pages}} remaining"
                        values={{
                          numFaxPagesLeft,
                        }} />
                    ) : (
                      <FormattedMessage
                        id=""
                        description="Text which informs the user they have unlimited faxes"
                        defaultMessage="Unlimited faxes remaining" />
                    )}
                  </Text>
                </Menu.Row>
                <Menu.Row>
                  <Button
                    variant="transparent"
                    href={HfReactHelper.urlHelper('account/gettingStarted', site)}
                    data-qa-ref="global-acct-menu-get-started-link"
                  >
                    <FormattedMessage
                      id=""
                      description="Text for a button that when pressed will bring the user to the Getting Started page"
                      defaultMessage="Get free pages" />
                  </Button>
                </Menu.Row>
              </>
            )}
          </Menu.Segment>
          {isSign && (
            <Menu.Segment>
              <Menu.Row>
                {signatureGuidToUse ? (
                  <>
                    <Spacer bottom={SpacerSize.SM}>
                      <img src={HfReactHelper.urlHelper(`attachment/view?sig_guid=${signatureGuidToUse}`, site)} />
                    </Spacer>
                    <Button
                      variant="transparent"
                      onClick={showSignatureModal}
                      data-qa-ref="global-acct-menu-create-or-edit-signature-btn"
                    >
                      <FormattedMessage
                        id=""
                        description="Text for a button that when pressed will guide the user in editing their signature"
                        defaultMessage="Edit signature" />
                    </Button>
                  </>
                ) : (
                  <Button
                    variant="opacity"
                    onClick={showSignatureModal}
                    data-qa-ref="global-acct-menu-create-or-edit-signature-btn"
                  >
                    <FormattedMessage
                      id=""
                      description="Text for a button that when pressed will guide the user in creating their own signature"
                      defaultMessage="Create a signature" />
                  </Button>
                )}
              </Menu.Row>
            </Menu.Segment>
          )}
          <Menu.Segment>
            {/* Admin console */}
            {canAccessAdminConsole && (
              <Menu.LinkItem
                href={HfReactHelper.urlHelper('admin', site)}
                data-qa-ref="global-acct-menu-admin-console-link"
              >
                <Text size="large">
                  <FormattedMessage
                    id=""
                    description="Text for a button that when pressed will bring the user to the Admin Console page"
                    defaultMessage="Admin console" />
                </Text>
              </Menu.LinkItem>
            )}
            {/* Settings */}
            <Menu.LinkItem
              href={HfReactHelper.urlHelper('home/myAccount', site)}
              data-qa-ref="global-acct-menu-settings-link"
            >
              <Text size="large">
                <FormattedMessage
                  id=""
                  description="Text for a button that when pressed will bring the user settings page"
                  defaultMessage="Settings" />
              </Text>
            </Menu.LinkItem>
            {/* Sign out */}
            <Menu.LinkItem
              href={HfReactHelper.urlHelper('account/logOut', site)}
              data-qa-ref="global-acct-menu-sign-out-link"
            >
              <Text size="large">
                <FormattedMessage
                  id=""
                  description="Text for a button that when pressed will log the user out"
                  defaultMessage="Sign out" />
              </Text>
            </Menu.LinkItem>
          </Menu.Segment>
          {isSignatureModalOpen && (
            <SignatureModal
              {...signatureModalProps}
              app={app}
              onRequestClose={() => setisSignatureModalOpen(false)} />
          )}
        </div>
      </RebrandTheme>
    </DbxGlobalHeader.AccountMenu>
  );
}

export default GlobalHeaderAccountMenu;
